import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { Dialog } from '../../ui/components/Dialog';
import { WalletSigningModal } from './WalletSigningModal';
import { type XamanPayloadDetails } from './XamanSigningModal';
import { toSigningPayload } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import { FeatherX } from '@subframe/core';

export interface TransactionFees {
  amount: number;
  platformFee: number;
  brokerFee?: number;
  networkFee: number;
  totalCost: number;
  isCafeBrokered?: boolean; // @deprecated - use isMarketplaceBrokered
  isMarketplaceBrokered?: boolean;
  feePercentage?: number;
  marketplaceName?: string;
}

export interface NFTDetails {
  tokenId: string;
  name: string;
  imageUrl?: string;
  collectionName?: string;
}

export type { XamanPayloadDetails };
export type TransactionType = 'buy' | 'sell' | 'transfer' | 'accept_offer' | 'create_offer';
export type TransactionStage = 'confirmation' | 'signing' | 'processing' | 'success' | 'error';

export interface TransactionConfirmationProps {
  // Transaction details
  type: TransactionType;
  nft: NFTDetails;
  fees: TransactionFees;

  // Optional destination (for transfers)
  destination?: string;

  // Stage
  stage?: TransactionStage;
  /** Normalized signing payload (any wallet provider). Preferred over xamanPayload. */
  payload?: SigningPayload;
  /** @deprecated pass `payload` (use toSigningPayload() to normalize) instead. */
  xamanPayload?: XamanPayloadDetails;

  // Actions
  onConfirm: () => void;
  onCancel: () => void;
  onSuccess?: (txHash: string) => void;
  onRejected?: () => void;
  onExpired?: () => void;

  // State
  processing?: boolean;

  // Styling
  className?: string;

  // Guest purchase (no push notification, QR code only)
  isGuest?: boolean;
}

export const TransactionConfirmation: React.FC<TransactionConfirmationProps> = ({
  type,
  nft,
  fees,
  destination,
  stage = 'confirmation',
  payload,
  xamanPayload,
  onConfirm,
  onCancel,
  onSuccess,
  onRejected,
  onExpired,
  processing = false,
  className = '',
  isGuest = false,
}) => {
  const getTransactionTitle = (): string => {
    switch (type) {
      case 'buy':
        return 'Confirm Purchase';
      case 'sell':
        return 'Confirm Listing';
      case 'transfer':
        return 'Confirm Transfer';
      case 'accept_offer':
        return 'Confirm Accept Offer';
      case 'create_offer':
        return 'Confirm Create Offer';
      default:
        return 'Confirm Transaction';
    }
  };

  const getActionLabel = (): string => {
    switch (type) {
      case 'buy':
        return 'Buy Now';
      case 'sell':
        return 'List for Sale';
      case 'transfer':
        return 'Transfer';
      case 'accept_offer':
        return 'Accept Offer';
      case 'create_offer':
        return 'Create Offer';
      default:
        return 'Confirm';
    }
  };

  const formatPrice = (price: number): string => {
    return `${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })} XRP`;
  };

  const formatAddress = (address: string): string => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  // Support both new isMarketplaceBrokered and deprecated isCafeBrokered
  const isMarketplace = fees.isMarketplaceBrokered ?? fees.isCafeBrokered ?? false;
  const feePercentage = fees.feePercentage ?? (isMarketplace ? 1.589 : 0.5);
  const marketplaceLabel = fees.marketplaceName
    ? `${fees.marketplaceName} Fee (${feePercentage}%):`
    : isMarketplace ? `Marketplace Fee (${feePercentage}%):` : `Platform Fee (${feePercentage}%):`;

  // Get custom message based on transaction type
  const getSigningMessage = (): string => {
    switch (type) {
      case 'buy':
        return 'Scan the QR code with Xaman and sign to purchase';
      case 'sell':
        return 'Scan the QR code with Xaman and sign to list for sale';
      case 'transfer':
        return 'Scan the QR code with Xaman and sign to transfer';
      case 'accept_offer':
        return 'Scan the QR code with Xaman and sign to accept offer';
      case 'create_offer':
        return 'Scan the QR code with Xaman and sign to submit offer';
      default:
        return 'Scan the QR code with Xaman and sign to complete transaction';
    }
  };

  // If in signing stage, show the signing modal instead
  const signingPayload = payload ?? (xamanPayload ? toSigningPayload(xamanPayload) : undefined);
  if (stage === 'signing' && signingPayload) {
    return (
      <WalletSigningModal
        open={true}
        onClose={onCancel}
        onSuccess={onSuccess}
        onRejected={onRejected}
        onExpired={onExpired}
        title={getTransactionTitle()}
        payload={signingPayload}
        message={getSigningMessage()}
        className={className}
      />
    );
  }

  // Otherwise show confirmation dialog
  return (
    <Dialog open={true} onOpenChange={(open: boolean) => !open && onCancel()}>
      <Dialog.Content className="w-full max-w-[550px]">
        {/* Header with close button */}
        <div className="flex w-full items-center justify-between px-6 py-4">
          <span className="text-heading-3 font-heading-3 text-default-font">{getTransactionTitle()}</span>
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded hover:bg-neutral-100 transition-colors"
            aria-label="Close"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>
        <div className="w-full h-px bg-neutral-border" />

        {/* Body */}
        <div className="flex flex-col gap-6 p-6">
          {/* NFT Preview - Clickable */}
          <Link
            to={`/nfts/${nft.tokenId}`}
            className="flex items-center gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-4 hover:bg-neutral-50 transition-colors"
          >
            <div className="h-20 w-20 flex-shrink-0 rounded-md overflow-hidden bg-neutral-100">
              {nft.imageUrl ? (
                <img
                  src={nft.imageUrl}
                  alt={nft.name}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-subtext-color">
                  <span className="text-caption font-caption">No Image</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-body-bold font-body-bold text-default-font truncate mb-1">{nft.name}</h3>
              {nft.collectionName && (
                <p className="text-body font-body text-subtext-color truncate mb-0.5">{nft.collectionName}</p>
              )}
              <p className="text-caption font-caption text-subtext-color font-mono truncate">{formatAddress(nft.tokenId)}</p>
            </div>
          </Link>

          {/* Destination (for transfers) */}
          {destination && (
            <div>
              <h4 className="text-caption-bold font-caption-bold text-subtext-color uppercase mb-2">Transfer To</h4>
              <div className="flex items-center gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
                <svg className="h-5 w-5 text-subtext-color flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span className="font-mono text-body font-body text-default-font">{formatAddress(destination)}</span>
              </div>
            </div>
          )}

          {/* Fee Breakdown */}
          <div className="rounded-md border border-solid border-neutral-border bg-default-background p-5">
            <h4 className="text-caption-bold font-caption-bold text-subtext-color uppercase mb-4">Transaction Details</h4>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-body font-body text-subtext-color">
                  {type === 'buy' ? 'Purchase Price:' : type === 'sell' ? 'Sale Price:' : 'Amount:'}
                </span>
                <span className="text-body-bold font-body-bold text-default-font">{formatPrice(fees.amount)}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-body font-body text-subtext-color">
                  {marketplaceLabel}
                </span>
                <span className="text-body-bold font-body-bold text-default-font">{formatPrice(fees.platformFee)}</span>
              </div>

              {fees.brokerFee && fees.brokerFee > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-body font-body text-subtext-color">Broker Fee:</span>
                  <span className="text-body-bold font-body-bold text-default-font">{formatPrice(fees.brokerFee)}</span>
                </div>
              )}

              <div className="h-px bg-neutral-border my-2"></div>

              <div className="flex items-center justify-between">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {type === 'buy' ? 'Total Cost:' : type === 'sell' ? 'You Receive:' : 'Total:'}
                </span>
                <span className="text-heading-2 font-heading-2 text-brand-600">
                  {formatPrice(fees.totalCost)}
                </span>
              </div>
            </div>
          </div>

          {/* Guest Purchase Notice */}
          {isGuest && (
            <div className="flex items-start gap-3 rounded-md bg-amber-50 border border-solid border-amber-200 p-4">
              <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <p className="text-body-bold font-body-bold text-amber-900 mb-1">Guest Purchase</p>
                <p className="text-caption font-caption text-amber-800">
                  You are purchasing as a guest. Scan the QR code with your Xaman wallet to complete the purchase.
                  No push notification will be sent - you must scan the code.
                </p>
              </div>
            </div>
          )}

          {/* Important Notice */}
          <div className="flex items-start gap-3 rounded-md bg-brand-50 border border-solid border-brand-200 p-4">
            <svg className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-body-bold font-body-bold text-brand-900 mb-1">Sign with Xaman</p>
              <p className="text-caption font-caption text-brand-800">
                You'll need to sign this transaction in your Xaman wallet to complete it.
                {type === 'transfer' && ' This action cannot be undone.'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="w-full h-px bg-neutral-border" />
        <div className="flex w-full items-center justify-end gap-3 px-6 py-5">
          <Button
            variant="neutral-secondary"
            size="large"
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            size="large"
            onClick={onConfirm}
            disabled={processing}
            loading={processing}
          >
            {getActionLabel()}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
